import { memo, useRef, useState } from 'react'
import { FaRegClock } from 'react-icons/fa'
import { MdKeyboardArrowRight, MdOutlineQuestionMark } from 'react-icons/md'
import { useShallow } from 'zustand/react/shallow'
import { camelToKebabCase } from '../../../helpers/functions/camel-to-kebab'
import {
  useTranslate,
  type Language,
} from '../../../hooks/language/use-translate'
import { useCreateGameBoxRoom } from '../../../hooks/queries/games/games-boxes-rooms/use-create-game-box-room'
import type {
  TypeGameBoxLocal,
  TypeGameBoxSent,
} from '../../../services/games/games-boxes-rooms/types'
import { useGlobalStore } from '../../../store/global-store'
import { SelectableButton } from './components/button'
import { ModalDescription } from './modals/modal-description'
import { ModalHistory } from './modals/modal-history'
import { ModalLocal } from './modals/modal-local'
import { ModalConditions } from './modals/modal-public'

export const ModalGameBox = memo(() => {
  const [activeTab] = useState<'division'>('division')

  const [showConditions, setShowConditions] = useState({
    visible: false,
    selectedValue: 'all' as TypeGameBoxSent,
  })

  const [showLocal, setShowLocal] = useState({
    visible: false,
    selectedValue: 'global' as TypeGameBoxLocal,
  })

  const [showHistory, setShowHistory] = useState(false)
  const [showDescription, setShowDescription] = useState(false)

  const [selectedCoin, setSelectedCoin] = useState(0)
  const [selectedPeople, setSelectedPeople] = useState(0)
  const [selectedTimer, setSelectedTimer] = useState(0)

  const inputMessageRef = useRef<HTMLInputElement>(null)

  const { translate } = useTranslate()
  const { create } = useCreateGameBoxRoom()

  const { liveId } = useGlobalStore(
    useShallow((state) => ({
      liveId: state.liveId,
    })),
  )

  const config = {
    division: {
      coins: [10, 20, 30, 40, 50, 60, 70, 100],
      people: [5, 10, 50, 100],
      times: [1, 3, 5],
      titleCoin: translate('modal-game-box-coins-member'),
      titleGift: translate('modal-game-box-gift-member'),
      button: (t: number | null) => (t ? `OK 🪙 ${t}` : 'OK 🪙 —'),
    },

    // draw: {
    //   coins: [50, 100, 200, 500, 1000],
    //   people: [10, 20, 100, 200, 500],
    //   times: [1, 3, 5],
    //   titleCoin: translate('modal-game-box-total-coins'),
    //   titleGift: translate('modal-game-box-available-gifts'),
    //   button: (t: number | null) => {
    //     return t
    //       ? `${translate('modal-game-box-raffle')} 🎲 (${t} coins)`
    //       : `${translate('modal-game-box-raffle')} 🎲`
    //   },
    // },
  }

  const {
    coins,
    people,
    times,
    titleCoin,
    titleGift,
    button,
    //
  } = config[activeTab]

  const total =
    selectedCoin && selectedPeople ? selectedCoin * selectedPeople : null

  const disabled = !selectedCoin || !selectedPeople || !selectedTimer

  if (showHistory) {
    return <ModalHistory handleBack={() => setShowHistory(false)} />
  }

  if (showDescription) {
    return <ModalDescription handleBack={() => setShowDescription(false)} />
  }

  async function handleSubmit() {
    if (liveId && total) {
      const response = await create({
        liveId,
        quantityPeopleEarning: selectedPeople,
        timeForDistribute: selectedTimer * 60, // minutes to seconds
        typeGameBoxLocal: showLocal.selectedValue,
        typeGameBoxSent: showConditions.selectedValue,
        valueTotalDistribute: total,
        ...(inputMessageRef.current && {
          message: inputMessageRef.current.value,
        }),
      })

      console.log(response)
    }
  }

  return (
    <>
      <div className="w-screen h-screen flex flex-col z-50">
        <div className="bg-white h-full rounded-t-3xl p-5 flex flex-col overflow-y-auto">
          {/* Tabs */}
          <div className="flex justify-center gap-8 mb-4 relative">
            <div className="flex flex-row gap-2 absolute top-1 right-0">
              <button onClick={() => setShowHistory(true)}>
                <FaRegClock />
              </button>

              <button onClick={() => setShowDescription(true)}>
                <MdOutlineQuestionMark />
              </button>
            </div>
          </div>

          {/* Coins */}
          <div className="mb-5">
            <p className="text-gray-700 font-semibold mb-2">{titleCoin}</p>

            <SelectableButton
              items={coins}
              selected={selectedCoin}
              onSelect={setSelectedCoin}
              prefix="🪙"
            />
          </div>

          <div className="mb-5">
            <p className="text-gray-700 font-semibold mb-2">{titleGift}</p>

            <SelectableButton
              items={people}
              selected={selectedPeople}
              onSelect={setSelectedPeople}
              prefix="🎁"
            />

            {/* <button className="mt-2 px-4 py-2 rounded-full border border-gray-300 text-gray-600 font-medium">
              🎁 personalizado
            </button> */}
          </div>

          <div className="mb-5">
            <p className="text-gray-700 font-semibold mb-2">Local</p>

            <button
              onClick={() => {
                setShowLocal({
                  visible: true,
                  selectedValue: 'global',
                })
              }}
              className="w-full border border-gray-300 rounded-lg p-3 flex justify-between items-center"
            >
              <span className="text-gray-600">
                {translate(`modal-local-${showLocal.selectedValue}`)}
              </span>

              <span className="text-gray-400">
                <MdKeyboardArrowRight />
              </span>
            </button>
          </div>

          <div className="mb-5">
            <p className="text-gray-700 font-semibold mb-2">
              {translate('modal-game-box-conditions')}
            </p>

            <button
              onClick={() =>
                setShowConditions({
                  visible: true,
                  selectedValue: 'all',
                })
              }
              className="w-full border border-gray-300 rounded-lg p-3 flex justify-between items-center"
            >
              <span className="text-gray-600">
                {translate(
                  `modal-condition-${camelToKebabCase(showConditions.selectedValue)}` as Language,
                )}
              </span>

              <span className="text-gray-400">
                <MdKeyboardArrowRight />
              </span>
            </button>
          </div>

          {showConditions.selectedValue === 'sentMessage' && (
            <div className="mb-5">
              <p className="text-gray-700 font-semibold mb-2">Mensagem</p>

              <input
                ref={inputMessageRef}
                className="w-full border border-gray-300 rounded-lg p-3 flex justify-between items-center"
              />
            </div>
          )}

          <div className="mb-8">
            <p className="text-gray-700 font-semibold mb-2">
              {translate('modal-game-box-countdown')}
            </p>

            <SelectableButton
              items={times}
              selected={selectedTimer}
              prefix="⏱️"
              sufix="min"
              onSelect={setSelectedTimer}
            />
          </div>

          <div className="flex flex-1 items-center justify-center">
            <button
              disabled={disabled}
              onClick={handleSubmit}
              className={`w-full font-semibold p-3 rounded-full transition ${
                disabled
                  ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                  : 'bg-blue-600 text-white'
              }`}
            >
              {button(total)}
            </button>
          </div>
        </div>
      </div>

      {showLocal.visible && (
        <ModalLocal
          onSelect={(typeGameBoxLocal) => {
            setShowLocal({
              visible: false,
              selectedValue: typeGameBoxLocal,
            })
          }}
          onClose={() => {
            setShowLocal((prevState) => ({
              ...prevState,
              visible: false,
            }))
          }}
        />
      )}

      {showConditions.visible && (
        <ModalConditions
          onSelect={(typeGameBoxSent) => {
            setShowConditions({
              visible: false,
              selectedValue: typeGameBoxSent,
            })
          }}
          onClose={() => {
            setShowConditions((prevState) => ({
              ...prevState,
              visible: false,
            }))
          }}
        />
      )}
    </>
  )
})

ModalGameBox.displayName = 'ModalDistribuition'
